import { ChangeEvent, memo } from "react";
import { useForm } from "react-hook-form";
import { MiDetailFields, MiModal } from "@miplatform/components";
import { BUTTON_VARIANTS, COLORS, INPUT_TYPES } from "@miplatform/constants";
import { CalendarType, ICalendarSet } from "@miplatform/types";
import { mdiHelpCircleOutline } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { ICalendarSetDuplicateOptions } from "../ConfigCalendarSetList.types";

export const ConfigDuplicateCalendarModal = memo(
  ({
    isLoading,
    open,
    calendar,
    onConfirm,
    onClose,
  }: ConfigDuplicateCalendarModalProps) => {
    const theme = useTheme();
    const { control, setValue, getValues } = useForm({
      mode: "onChange",
      defaultValues: {
        schedulerUsers: false,
        assignableUsers: false,
        tabsAndTrays: false,
        serviceArea: false,
        selectAll: false,
      },
    });

    const isHourlyView =
      calendar.calendarConfigs?.[0].calendarType === CalendarType.Scheduler;

    const allConfigField = [
      {
        label: "All Configurations",
        name: "selectAll",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "100%",
        handleChange: ({
          target: { checked },
        }: ChangeEvent<HTMLInputElement>) => {
          setValue("selectAll", checked);
          if (checked) {
            setValue("schedulerUsers", checked);
            setValue("assignableUsers", isHourlyView && checked);
            setValue("tabsAndTrays", checked);
            setValue("serviceArea", checked);
          }
        },
      },
    ];

    const duplicateFields = [
      {
        label: "Calendar Users",
        name: "schedulerUsers",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "100%",
        style: {
          paddingTop: 0,
          paddingBottom: 0,
        },
        handleChange: ({
          target: { checked },
        }: ChangeEvent<HTMLInputElement>) => {
          if (!checked) {
            setValue("selectAll", false);
          }
          setValue("schedulerUsers", checked);
        },
      },
      {
        label: "Assignable Users",
        name: "assignableUsers",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "100%",
        handleChange: ({
          target: { checked },
        }: ChangeEvent<HTMLInputElement>) => {
          if (!checked) {
            setValue("selectAll", false);
          }
          setValue("assignableUsers", checked);
        },
        ...(!isHourlyView && { style: { display: "none" } }),
      },
      {
        label: "Custom Tabs / Trays",
        name: "tabsAndTrays",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "100%",
        handleChange: ({
          target: { checked },
        }: ChangeEvent<HTMLInputElement>) => {
          if (!checked) {
            setValue("selectAll", false);
          }
          setValue("tabsAndTrays", checked);
        },
        ...(isHourlyView && {
          style: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
      },
      {
        label: "Service Area",
        name: "serviceArea",
        formControl: control,
        fieldType: INPUT_TYPES.CHECKBOX,
        width: "100%",
        handleChange: ({
          target: { checked },
        }: ChangeEvent<HTMLInputElement>) => {
          if (!checked) {
            setValue("selectAll", false);
          }
          setValue("serviceArea", checked);
        },
        ...(!isHourlyView && {
          style: {
            paddingTop: 0,
            paddingBottom: 0,
          },
        }),
      },
    ];

    return (
      <MiModal
        title="Duplicate Calendar"
        titleIcon={mdiHelpCircleOutline}
        leftIconColor={theme.palette.primary.main}
        open={open}
        closeModal={onClose}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            color: COLORS.BLACK,
            onClick: onClose,
          },
          {
            name: "Continue",
            variant: BUTTON_VARIANTS.CONTAINED,
            onClick: () => onConfirm(calendar, getValues()),
            disabled: isLoading,
          },
        ]}
      >
        <Typography
          fontSize={theme.fontSize.medium}
          color={theme.palette.grey.medium}
          pb={2}
        >
          You are duplicating{" "}
          <Typography
            fontSize={theme.fontSize.medium}
            color={theme.palette.grey.medium}
            fontWeight={theme.fontWeight.bold}
            component="span"
          >
            {calendar.name}
          </Typography>
          . Select the configurations you would like to copy:
        </Typography>
        <Box ml={-2}>
          <MiDetailFields detailFields={allConfigField} />
        </Box>
        <Box pl={4}>
          <MiDetailFields detailFields={duplicateFields} />
        </Box>
      </MiModal>
    );
  }
);

interface ConfigDuplicateCalendarModalProps {
  isLoading: boolean;
  open: boolean;
  calendar: ICalendarSet;
  onConfirm: (
    calendarSet: ICalendarSet,
    options: ICalendarSetDuplicateOptions
  ) => void;
  onClose: () => void;
}
